import isEmpty from 'lodash/isEmpty';
import { JobSpeciality, translateLanguages } from '@clipr/lib';
import { action, computed, makeObservable, observable } from 'mobx';
import { Store } from '../../store/store';
import { BindingProps, StoreNode } from '../../store';
import { input, inputGroup, InputGroupState, InputState } from '../../components/input';
import { UploadTask } from '../../services/upload/uploadTask';
import { UploadFile } from '../../services/upload/uploadService';
import { Team } from '../../entities';
import { DropdownItemObject } from '../../components/input/dropdownInput';

const TranslateLanguageItems: DropdownItemObject[] = translateLanguages
  .map(({ name, code }) => ({ value: code, label: name }));

type DocumentUploadFileItemProps = BindingProps<{
  teamId?: string;
  folderId?: string;
  file: File;
}>;

export class DocumentUploadFileItemState
  extends StoreNode {

  constructor(store: Store, props: DocumentUploadFileItemProps) {
    super(store, props);
    makeObservable(this, {
      task: observable,
      isStaged: computed,
      isActive: computed,
      isCompleted: computed,
      isFailed: computed,
      showInStaging: computed,
      inProgress: computed,
      teamId: computed,
      folderId: computed,
      team: computed,
      submit: action,
      init: action,
    });

    this.sourceLanguageInput = input(this, {
      name: 'sourceLanguage',
      placeholder: 'Choose a language',
      selectorItems: TranslateLanguageItems,
      showStatusMessage: true,
      isRequired: true,
      statusMessage: (inp: InputState) =>
        inp.status === 'error' && inp.showStatusIcons ? inp.error?.toString() : '',
      error: (inp: InputState) => {
        if (isEmpty(inp.value)) return 'Required field';
      },
      export: (self: InputState) => self.normValue,
    });

    this.targetLanguageInput = input(this, {
      name: 'targetLanguage',
      placeholder: 'Choose a language',
      selectorItems: TranslateLanguageItems,
      showStatusMessage: true,
      isRequired: true,
      statusMessage: (inp: InputState) =>
        inp.status === 'error' && inp.showStatusIcons ? inp.error?.toString() : '',
      error: (inp: InputState) => {
        if (isEmpty(inp.value)) return 'Required field';
        if (inp.normValue === this.sourceLanguageInput.normValue)
          return 'Must differ from source';
      },
      export: (self: InputState) => self.normValue,
    });

    this.tagsInput = input(this, {
      name: 'tags',
      label: 'Tags',
    });

    this.form = inputGroup(this, {
      name: 'documentForm',
      inputs: [
        this.sourceLanguageInput,
        this.targetLanguageInput,
        this.tagsInput,
      ],
      isSubmitDisabled: () => {
        return (
          this.form.isSubmitting ||
          this.form.hasTouchedErrorInputs
        );
      }
    });

    this.file = this.props.file;
    this.init();
  }

  task: UploadTask | null = null;

  readonly form: InputGroupState;
  readonly file: File;

  readonly sourceLanguageInput: InputState;
  readonly targetLanguageInput: InputState;
  readonly tagsInput: InputState;

  get isStaged(): boolean {
    return !this.task;
  }

  get isActive(): boolean {
    return !!this.task?.isActive;
  }

  get isCompleted(): boolean {
    return !!this.task?.isCompleted;
  }

  get isFailed(): boolean {
    return !!this.task?.isFailed;
  }

  get showInStaging(): boolean {
    return this.isStaged;
  }

  get inProgress(): boolean {
    return this.isActive || this.isFailed;
  }

  get teamId(): string | null {
    return this.resolvedProps.teamId;
  }

  get folderId(): string | null {
    return this.resolvedProps.folderId;
  }

  get team(): Team | null {
    return this.store.teamManager.getTeam(this.teamId);
  }

  submit(): UploadTask {
    const formData = this.form.export();

    const uploadFile: UploadFile = {
      file: this.file,
      jobSpeciality: JobSpeciality.DocumentTranslation,
      videoType: undefined,
      sourceLanguageCode: formData.sourceLanguage || undefined,
      targetLanguageCode: formData.targetLanguage || undefined,
      folderId: this.folderId ?? undefined,
      tags: this.tagsInput.value?.trim() || undefined,
    };

    this.task = this.store.uploadService.createTask(uploadFile, {
      teamId: this.teamId || undefined,
    });

    return this.task;
  }

  init = () => {
    const teamLang = this.team?.defaultTranslationLanguage;
    const targetLang = teamLang || 'es';
    const sourceLang = targetLang === 'en' ? 'es' : 'en';

    const defaultSource = TranslateLanguageItems.find(item => item.value === sourceLang);
    const defaultTarget = TranslateLanguageItems.find(item => item.value === targetLang);
    if (defaultSource) this.sourceLanguageInput.loadValue(defaultSource);
    if (defaultTarget) this.targetLanguageInput.loadValue(defaultTarget);
  }
}
